import DetailsEvents from "./details-event";
import EventForm from "./event-form";
import { useModal } from "@/app/shared/modal-views/use-modal";
import { PaginatedResult } from "@/Core/Client";
import { EventResponseDto } from "@/Dtos/event-dto";
import { useColorPresetName } from "@/layouts/settings/use-theme-color";
import type { CalendarEvent } from "@/types";
import cn from "@/utils/class-names";
import dayjs from "dayjs";
import { useCallback, useMemo } from "react";
import { Calendar, dayjsLocalizer } from "react-big-calendar";

const localizer = dayjsLocalizer(dayjs);

const calendarToolbarClassName =
    "[&_.rbc-toolbar_.rbc-toolbar-label]:whitespace-nowrap [&_.rbc-toolbar_.rbc-toolbar-label]:my-2 [&_.rbc-toolbar]:flex [&_.rbc-toolbar]:flex-col [&_.rbc-toolbar]:items-center @[56rem]:[&_.rbc-toolbar]:flex-row [&_.rbc-btn-group_button:hover]:bg-gray-300 [&_.rbc-btn-group_button]:duration-200 [&_.rbc-btn-group_button.rbc-active:hover]:bg-gray-600 dark:[&_.rbc-btn-group_button.rbc-active:hover]:bg-gray-300 [&_.rbc-btn-group_button.rbc-active:hover]:text-gray-50 dark:[&_.rbc-btn-group_button.rbc-active:hover]:text-gray-900 [@media(max-width:375px)]:[&_.rbc-btn-group:last-child_button]:!px-2.5 [&_.rbc-toolbar_>_*:last-child_>_button:focus]:!bg-primary [&_.rbc-toolbar_>_*:last-child_>_button:focus]:!text-gray-0 dark:[&_.rbc-toolbar_>_*:last-child_>_button:focus]:!text-gray-900 [&_.rbc-toolbar_>_*:last-child_>_button:hover]:!text-gray-900 dark:[&_.rbc-toolbar_>_*:last-child_>_button:hover]:!bg-gray-300 [&_.rbc-toolbar_>_*:last-child_>_button:hover]:!bg-gray-300 [&_.rbc-toolbar_>_*:last-child_>_button.rbc-active:hover]:!bg-primary-dark [&_.rbc-toolbar_>_*:last-child_>_button.rbc-active:hover]:!text-gray-0 dark:[&_.rbc-toolbar_>_*:last-child_>_button.rbc-active:hover]:!text-gray-900";

const rtcEventClassName =
    "[&_.rbc-event]:!text-gray-0 dark:[&_.rbc-event]:!text-gray-0 dark:[&_.rbc-toolbar_>_*:last-child_>_button.rbc-active:hover]:!text-gray-0 dark:[&_.rbc-toolbar_>_*:last-child_>_button.rbc-active:focus]:!text-gray-0";

export default function EventCalendarView({ data }: { data: PaginatedResult<EventResponseDto> }) {
    const events = data?.items.map((event) => ({
        id: event.id!,
        start: new Date(event.startDate),
        end: new Date(event.endDate),
        title: event.name,
        description: event.description,
        location: event.venue?.name ?? "No Venue",
        allDay: true,
    } as CalendarEvent)) ?? [];

    const { openModal } = useModal();
    const { colorPresetName } = useColorPresetName();

    const handleSelectSlot = useCallback(
        ({ start, end }: { start: Date; end: Date }) => {
            openModal({
                view: <EventForm startDate={start} endDate={end} />,
                customSize: "650px",
            });
        },
        [openModal],
    );

    const handleSelectEvent = useCallback(
        (event: CalendarEvent) => {
            openModal({
                view: <DetailsEvents event={event} />,
                customSize: "500px",
            });
        },
        [openModal],
    );

    const { views, scrollToTime, formats } = useMemo(
        () => ({
            views: {
                month: true,
                week: true,
                day: true,
                agenda: true,
            },
            scrollToTime: new Date(2025, 1, 1, 6),
            formats: {
                dateFormat: "D",
                weekdayFormat: (date: Date, culture: any, localizer: any) =>
                    localizer.format(date, "ddd", culture),
                dayFormat: (date: Date, culture: any, localizer: any) =>
                    localizer.format(date, "ddd M/D", culture),
                timeGutterFormat: (date: Date, culture: any, localizer: any) =>
                    localizer.format(date, "hh A", culture),
            },
        }),
        [],
    );

    return (
        <div className="@container">
            <Calendar
                localizer={localizer}
                events={events}
                views={views}
                formats={formats}
                startAccessor="start"
                endAccessor="end"
                min={new Date(2025, 0, 1, 8)}
                dayLayoutAlgorithm="no-overlap"
                onSelectEvent={handleSelectEvent}
                onSelectSlot={handleSelectSlot}
                selectable
                scrollToTime={scrollToTime}
                className={cn(
                    "h-[650px] md:h-[1000px]",
                    calendarToolbarClassName,
                    colorPresetName === "black" && rtcEventClassName,
                )}
            />
        </div>
    );
}

